import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ListChecks, Plus, Trash2, Pencil, AlertTriangle, LayoutGrid, TableProperties,
  Settings2, BellRing, UserRound, Users2, CalendarClock,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal, hariIniISO } from "@/lib/tanggal";
import {
  PageHeader, SearchInput, usePaginasi, Paginasi, unduhCsv, TombolEksporCsv,
  Modal, Field, inputCls, selectCls, textareaCls, usePinHapus, PinHapusDialog,
} from "./_shared";

/**
 * Task / Daily Mission — papan tugas khusus Marketing.
 *
 * TIGA HAL YANG SENGAJA TIDAK DIKERJAKAN DI LAYAR INI:
 *
 * 1. **Tidak ada penyaringan sisi-klien.** Server memotong daftar di 500 baris
 *    dan mengirim `total`/`terpotong`; menyaring hasil potongan itu di layar
 *    berarti baris ke-501 tidak akan pernah ketemu sementara layarnya melapor
 *    "tidak ada hasil" dengan yakin (CLAUDE.md). Semua filter — cari, status,
 *    prioritas, penerima, role, rentang deadline — dikirim ke server sebagai
 *    query, dan angka kartu ringkasan datang dari endpoint terpisah yang
 *    memakai filter yang SAMA.
 *
 * 2. **Tidak ada pengurutan sisi-klien.** Alasan yang sama: mengurutkan 500
 *    baris teratas bukan mengurutkan seluruh daftar. `urut` dikirim ke server.
 *
 * 3. **"Terlambat" tidak dihitung ulang di sini.** Backend mengirim
 *    `status_efektif`/`overdue`/`sisa_hari` yang sudah dihitung dari tanggal
 *    WIB server. Menghitungnya lagi dari `new Date()` di browser akan meleset
 *    sehari untuk pengguna WIB antara 00:00–07:00 (lihat lib/tanggal.js), dan
 *    dua sumber kebenaran untuk satu aturan sudah pernah menggigit repo ini.
 */

const STATUS_META = {
  todo: { label: "Belum Mulai", chip: "bg-slate-100 text-slate-700", kolom: "border-slate-300 bg-slate-50" },
  in_progress: { label: "Sedang Dikerjakan", chip: "bg-sky-100 text-sky-700", kolom: "border-sky-300 bg-sky-50" },
  completed: { label: "Selesai", chip: "bg-emerald-100 text-emerald-800", kolom: "border-emerald-300 bg-emerald-50" },
  overdue: { label: "Terlambat", chip: "bg-rose-100 text-rose-700", kolom: "border-rose-300 bg-rose-50" },
};
// Urutan kolom papan = perjalanan sebuah task. "Terlambat" ditaruh paling kanan
// supaya jadi kolom yang paling mencolok saat isinya bertambah.
const STATUS_KOLOM = ["todo", "in_progress", "completed", "overdue"];
// Hanya tiga yang bisa DISIMPAN — "overdue" turunan, backend menolaknya.
const STATUS_TERSIMPAN = ["todo", "in_progress", "completed"];

const PRIORITY_META = {
  low: { label: "Rendah", chip: "bg-slate-100 text-slate-600" },
  medium: { label: "Sedang", chip: "bg-sky-100 text-sky-700" },
  high: { label: "Tinggi", chip: "bg-amber-100 text-amber-800" },
  urgent: { label: "Mendesak", chip: "bg-rose-100 text-rose-700" },
};
const PRIORITIES = ["low", "medium", "high", "urgent"];

const emptyForm = () => ({
  id: null, nama: "", deskripsi: "", assignee_type: "user", assignee_user_id: "",
  role: "", priority: "medium", deadline: "", status: "todo", reminder_hari_sebelum: "",
});

function Chip({ meta, value }) {
  const m = meta[value] || { label: value || "-", chip: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${m.chip}`}>
      {m.label}
    </span>
  );
}

function labelPenerima(t) {
  if (t.assignee_type === "role") return t.role || "(role dihapus)";
  return t.assignee_nama || "-";
}

export default function Task() {
  const { can } = useAuth();
  // Kunci izin PERSIS sama dengan `require_permission`-nya di backend
  // (routers/marketing/task.py: MODUL = "marketing_task") — aturan #3 CLAUDE.md.
  const bolehKelola = can("marketing_task", "view");
  const bolehTambah = can("marketing_task", "create");
  const bolehEdit = can("marketing_task", "update");
  const bolehHapus = can("marketing_task", "delete");

  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const [fStatus, setFStatus] = useState("");
  const [fPriority, setFPriority] = useState("");
  const [fPenerima, setFPenerima] = useState("");
  const [fRole, setFRole] = useState("");
  const [fDari, setFDari] = useState("");
  const [fSampai, setFSampai] = useState("");
  const [milikSaya, setMilikSaya] = useState(false);
  const [urut, setUrut] = useState("deadline");
  const [form, setForm] = useState(null);
  // M1 (brief 8 Sep 2026): task yang sedang dibuka. Sebelum ini kartu dan baris
  // tabel TIDAK punya onClick sama sekali — satu-satunya pembuka adalah tombol
  // pensil yang digerbangi izin `update`, sehingga staf yang DIKIRIMI tugas
  // tidak punya cara apa pun melihat isinya. Endpoint `GET /marketing/tasks/{id}`
  // sudah lama ada berikut aturan "hanya task Anda", tapi belum pernah dipanggil
  // satu layar pun.
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailGagal, setDetailGagal] = useState(null);
  const [roleForm, setRoleForm] = useState(null); // {items:[{nama,user_ids}]}
  const [busy, setBusy] = useState(false);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();

  const paramsUmum = {
    ...(qDebounced.trim() ? { q: qDebounced.trim() } : {}),
    ...(fPriority ? { priority: fPriority } : {}),
    ...(fPenerima ? { assignee_user_id: fPenerima } : {}),
    ...(fRole ? { role: fRole } : {}),
    ...(fDari ? { deadline_dari: fDari } : {}),
    ...(fSampai ? { deadline_sampai: fSampai } : {}),
    ...(milikSaya ? { milik_saya: true } : {}),
  };
  const kunciUmum = [qDebounced, fPriority, fPenerima, fRole, fDari, fSampai, milikSaya];

  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "tasks", ...kunciUmum, fStatus, urut],
    () => api.get("/marketing/tasks", {
      params: { ...paramsUmum, ...(fStatus ? { status: fStatus } : {}), urut },
    }).then((r) => r.data),
  );

  const { data: ringkasan, refetch: refetchRingkasan } = useDataQuery(
    ["marketing", "tasks", "ringkasan", ...kunciUmum],
    () => api.get("/marketing/tasks/ringkasan", { params: paramsUmum }).then((r) => r.data),
  );

  const { data: roleData, refetch: refetchRole } = useDataQuery(
    ["marketing", "tasks", "roles"],
    () => api.get("/marketing/tasks/roles").then((r) => r.data),
  );

  // Endpoint kandidat digerbangi `marketing_task.create`/`update` — jangan
  // dipanggil kalau ujungnya 403 (aturan #1 CLAUDE.md).
  const { data: kandidat } = useDataQuery(
    ["marketing", "tasks", "kandidat"],
    () => api.get("/marketing/tasks/kandidat").then((r) => r.data),
    { enabled: bolehTambah || bolehEdit },
  );

  const rows = useMemo(() => (data && data.items) || [], [data]);
  const roleItems = useMemo(() => (roleData && roleData.items) || [], [roleData]);
  const kandidatItems = useMemo(() => (kandidat && kandidat.items) || [], [kandidat]);
  const totalServer = (data && data.total) || 0;
  const terpotong = !!(data && data.terpotong);

  const { page, setPage, totalPages, awal, akhir } = usePaginasi(rows.length, 15);
  const halaman = rows.slice(awal, akhir);

  const perKolom = useMemo(() => {
    const m = {};
    STATUS_KOLOM.forEach((s) => { m[s] = []; });
    rows.forEach((t) => {
      const s = t.status_efektif || t.status;
      if (!m[s]) m[s] = [];
      m[s].push(t);
    });
    return m;
  }, [rows]);

  const segarkan = () => { refetch(); refetchRingkasan(); };

  const eksporCsv = () => {
    if (terpotong) toast.warning(`Ekspor berisi ${rows.length} dari ${totalServer} baris — persempit filternya dulu kalau butuh lengkap.`);
    unduhCsv(`task-marketing-${hariIniISO()}.csv`, [
      { label: "Nama Task", value: (r) => r.nama },
      { label: "Penerima", value: (r) => labelPenerima(r) },
      { label: "Jenis Penerima", value: (r) => (r.assignee_type === "role" ? "Role" : "Perorangan") },
      { label: "Role", value: (r) => (r.roles_pemikul || []).join(" / ") },
      { label: "Prioritas", value: (r) => (PRIORITY_META[r.priority] || {}).label || r.priority },
      { label: "Deadline", value: (r) => r.deadline || "" },
      { label: "Status", value: (r) => (STATUS_META[r.status_efektif] || {}).label || r.status_efektif },
      { label: "Reminder (H-)", value: (r) => (r.reminder_hari_sebelum === null ? "" : r.reminder_hari_sebelum) },
      { label: "Dibuat", value: (r) => r.created_at },
    ], rows);
  };

  /** Buka satu task. Isinya diambil dari server (bukan dari baris daftar) supaya
   *  yang tampil adalah keadaan TERKINI — dan supaya aturan "hanya task Anda"
   *  di backend benar-benar dilewati, bukan cuma dipercaya dari daftar. */
  const bukaDetail = async (t) => {
    setDetailId(t.id);
    setDetail(null);
    setDetailGagal(null);
    try {
      const r = await api.get(`/marketing/tasks/${t.id}`);
      setDetail(r.data);
    } catch (e) {
      // Ditampilkan di dalam modal, BUKAN sebagai toast yang lalu menutup
      // modalnya — kalau tidak, gejalanya kembali jadi "diklik tidak muncul
      // apa-apa", persis keluhan yang sedang diperbaiki.
      setDetailGagal(e?.response?.data?.detail || "Task tidak bisa dibuka");
    }
  };
  const tutupDetail = () => { setDetailId(null); setDetail(null); setDetailGagal(null); };

  const bukaTambah = () => setForm(emptyForm());
  const bukaEdit = (t) => setForm({
    id: t.id, nama: t.nama, deskripsi: t.deskripsi || "",
    assignee_type: t.assignee_type || "user",
    assignee_user_id: t.assignee_user_id || "", role: t.role || "",
    priority: t.priority, deadline: t.deadline || "", status: t.status,
    reminder_hari_sebelum: t.reminder_hari_sebelum === null || t.reminder_hari_sebelum === undefined
      ? "" : String(t.reminder_hari_sebelum),
  });

  const simpan = async () => {
    if (!form.nama.trim()) { toast.error("Nama task wajib diisi"); return; }
    if (form.assignee_type === "user" && !form.assignee_user_id) { toast.error("Pilih penerima tugas"); return; }
    if (form.assignee_type === "role" && !form.role) { toast.error("Pilih role penerima tugas"); return; }
    setBusy(true);
    try {
      const isi = {
        nama: form.nama.trim(), deskripsi: form.deskripsi,
        assignee_type: form.assignee_type,
        assignee_user_id: form.assignee_type === "user" ? form.assignee_user_id : null,
        role: form.assignee_type === "role" ? form.role : null,
        priority: form.priority, status: form.status,
      };
      if (form.id) {
        // `null` di backend berarti "tidak diubah", jadi pengosongan dikirim
        // lewat penanda eksplisit — tanpa itu deadline yang telanjur diisi
        // tidak akan pernah bisa dikosongkan lagi dari layar.
        isi.hapus_deadline = !form.deadline;
        if (form.deadline) isi.deadline = form.deadline;
        isi.hapus_reminder = form.reminder_hari_sebelum === "";
        if (form.reminder_hari_sebelum !== "") isi.reminder_hari_sebelum = Number(form.reminder_hari_sebelum);
        await api.patch(`/marketing/tasks/${form.id}`, isi);
        toast.success("Task diperbarui");
      } else {
        isi.deadline = form.deadline || null;
        isi.reminder_hari_sebelum = form.reminder_hari_sebelum === "" ? null : Number(form.reminder_hari_sebelum);
        await api.post("/marketing/tasks", isi);
        toast.success("Task ditambahkan");
      }
      setForm(null); segarkan();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan task"); }
    finally { setBusy(false); }
  };

  const gantiStatus = async (t, status) => {
    try {
      await api.patch(`/marketing/tasks/${t.id}`, { status });
      toast.success(`Status: ${(STATUS_META[status] || {}).label || status}`);
      segarkan();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };

  const hapus = async (t) => {
    if (!window.confirm(`Hapus task "${t.nama}"?`)) return;
    const aksi = (pin) => api.delete(`/marketing/tasks/${t.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_task", targetId: t.id, ringkasan: `Hapus task ${t.nama}` });
    if (langsung) { toast.success("Task dihapus"); segarkan(); }
  };

  const simpanRole = async (paksa = false) => {
    setBusy(true);
    try {
      const r = await api.put("/marketing/tasks/roles", { items: roleForm.items }, { params: paksa ? { paksa: true } : {} });
      toast.success(r.data.message || "Daftar role disimpan");
      setRoleForm(null); refetchRole(); segarkan();
    } catch (e) {
      const st = e?.response?.status;
      const pesan = e?.response?.data?.detail || "Gagal menyimpan daftar role";
      // 409 = ada role yang masih dipakai task. Ditawarkan, bukan dipaksakan
      // diam-diam: task lama akan tetap memegang nama role lamanya.
      if (st === 409 && window.confirm(`${pesan}\n\nLanjutkan tetap menyimpan?`)) {
        setBusy(false);
        await simpanRole(true);
        return;
      }
      toast.error(pesan);
    } finally { setBusy(false); }
  };

  const kartuRingkasan = STATUS_KOLOM.map((s) => ({
    key: s, label: STATUS_META[s].label,
    n: ringkasan && ringkasan.per_status ? (ringkasan.per_status[s] || 0) : 0,
  }));

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-task">
      <PageHeader
        icon={ListChecks} title="Task / Daily Mission"
        desc="Tugas harian tim Marketing — bisa ditujukan ke satu orang atau ke sebuah role. Status 'Terlambat' dihitung otomatis dari tenggatnya, tidak perlu diubah manual."
        actions={<>
          <TombolEksporCsv onClick={eksporCsv} testid="mkt-task-export" />
          {bolehEdit && (
            <button onClick={() => setRoleForm({ items: roleItems.map((r) => ({ nama: r.nama, user_ids: r.user_ids || [] })) })}
                    data-testid="mkt-task-atur-role"
                    className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5">
              <Settings2 className="w-3.5 h-3.5" /> Atur Role
            </button>
          )}
          {bolehTambah && (
            <button onClick={bukaTambah} data-testid="mkt-task-tambah"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Tambah Task
            </button>
          )}
        </>}
      />

      {/* Kartu ringkasan — angkanya dari server, atas filter yang SAMA dengan tabel */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mkt-task-ringkasan">
        {kartuRingkasan.map((k) => (
          <button key={k.key} onClick={() => setFStatus(fStatus === k.key ? "" : k.key)}
                  data-testid={`mkt-task-kartu-${k.key}`}
                  className={`text-left bg-white border rounded-xl p-3 shadow-sm transition-colors ${fStatus === k.key ? "border-emerald-500 ring-1 ring-emerald-200" : "border-slate-200 hover:border-slate-300"}`}>
            <p className="text-[11px] text-slate-500 font-medium">{k.label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-0.5">{k.n}</p>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput value={q} onChange={setQ} placeholder="Cari nama task / penerima..." testid="mkt-task-cari" className="w-56" />
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="mkt-task-filter-status" className={`${selectCls} h-9 w-auto`}>
            <option value="">Semua Status</option>
            {STATUS_KOLOM.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <select value={fPriority} onChange={(e) => setFPriority(e.target.value)} data-testid="mkt-task-filter-priority" className={`${selectCls} h-9 w-auto`}>
            <option value="">Semua Prioritas</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
          </select>
          <select value={fRole} onChange={(e) => setFRole(e.target.value)} data-testid="mkt-task-filter-role" className={`${selectCls} h-9 w-auto`}>
            <option value="">Semua Role</option>
            {roleItems.map((r) => <option key={r.nama} value={r.nama}>{r.nama}</option>)}
          </select>
          {(bolehTambah || bolehEdit) && (
            <select value={fPenerima} onChange={(e) => setFPenerima(e.target.value)} data-testid="mkt-task-filter-penerima" className={`${selectCls} h-9 w-auto`}>
              <option value="">Semua Penerima</option>
              {kandidatItems.map((u) => <option key={u.id} value={u.id}>{u.nama}</option>)}
            </select>
          )}
          <input type="date" value={fDari} onChange={(e) => setFDari(e.target.value)} data-testid="mkt-task-filter-dari"
                 title="Deadline dari" className={`${inputCls} h-9 w-auto`} />
          <input type="date" value={fSampai} onChange={(e) => setFSampai(e.target.value)} data-testid="mkt-task-filter-sampai"
                 title="Deadline sampai" className={`${inputCls} h-9 w-auto`} />
          {bolehKelola && (
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 h-9 px-2">
              <input type="checkbox" checked={milikSaya} onChange={(e) => setMilikSaya(e.target.checked)}
                     data-testid="mkt-task-filter-saya" className="w-3.5 h-3.5 accent-emerald-600" />
              Hanya task saya
            </label>
          )}
          <select value={urut} onChange={(e) => setUrut(e.target.value)} data-testid="mkt-task-urut" className={`${selectCls} h-9 w-auto`}>
            <option value="deadline">Urut: Deadline terdekat</option>
            <option value="prioritas">Urut: Prioritas tertinggi</option>
            <option value="terbaru">Urut: Terbaru dibuat</option>
          </select>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1">
          <button onClick={() => setView("kanban")} data-testid="mkt-task-view-kanban"
                  className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${view === "kanban" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Papan
          </button>
          <button onClick={() => setView("tabel")} data-testid="mkt-task-view-tabel"
                  className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${view === "tabel" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <TableProperties className="w-3.5 h-3.5" /> Tabel
          </button>
        </div>
      </div>

      {terpotong && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="mkt-task-terpotong">
          Menampilkan {rows.length} dari {totalServer} task yang cocok. Persempit filternya supaya daftarnya lengkap —
          yang tidak tampil di sini juga tidak ikut terekspor.
        </p>
      )}

      {view === "kanban" ? (
        <LoadingState loading={loading} error={error} onRetry={segarkan} varian="kartu" jumlahKartu={4} data-testid="mkt-task-kanban-loading">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="mkt-task-kanban">
            {STATUS_KOLOM.map((s) => (
              <div key={s} className={`rounded-xl border-2 ${STATUS_META[s].kolom} p-2.5 flex flex-col gap-2 min-h-[160px]`} data-testid={`mkt-task-kolom-${s}`}>
                <div className="flex items-center justify-between px-1">
                  <p className="text-xs font-semibold text-slate-700">{STATUS_META[s].label}</p>
                  <span className="text-[11px] font-mono text-slate-500">{perKolom[s].length}</span>
                </div>
                <div className="space-y-2">
                  {perKolom[s].map((t) => (
                    <div key={t.id} data-testid={`mkt-task-card-${t.id}`}
                         role="button" tabIndex={0}
                         onClick={() => bukaDetail(t)}
                         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); bukaDetail(t); } }}
                         className="bg-white rounded-lg border border-slate-200 p-2.5 shadow-sm space-y-1.5 cursor-pointer hover:border-emerald-400 hover:shadow-md transition-colors">
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-xs font-semibold text-slate-900 leading-snug">{t.nama}</p>
                        <Chip meta={PRIORITY_META} value={t.priority} />
                      </div>
                      <p className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                        {t.assignee_type === "role" ? <Users2 className="w-3 h-3" /> : <UserRound className="w-3 h-3" />}
                        {labelPenerima(t)}
                      </p>
                      {t.deadline && (
                        <p className={`text-[11px] inline-flex items-center gap-1 ${t.overdue ? "text-rose-600 font-medium" : "text-slate-500"}`}>
                          {t.overdue ? <AlertTriangle className="w-3 h-3" /> : <CalendarClock className="w-3 h-3" />}
                          {formatTanggal(t.deadline)}
                          {t.sisa_hari !== null && t.sisa_hari !== undefined && !t.overdue && t.status !== "completed"
                            ? ` · ${t.sisa_hari === 0 ? "hari ini" : `H-${t.sisa_hari}`}` : ""}
                        </p>
                      )}
                      {t.reminder_hari_ini && (
                        <p className="text-[10px] text-amber-700 inline-flex items-center gap-1">
                          <BellRing className="w-3 h-3" /> Pengingat jatuh hari ini
                        </p>
                      )}
                      {/* Klik di baris aksi TIDAK ikut membuka detail — kalau
                          merambat, memilih status akan langsung menimpa layar
                          dengan modal. */}
                      <div className="flex items-center justify-between pt-1 gap-1"
                           onClick={(e) => e.stopPropagation()}>
                        <select value={t.status} onChange={(e) => gantiStatus(t, e.target.value)}
                                data-testid={`mkt-task-status-${t.id}`}
                                className="h-7 text-[11px] rounded border border-slate-200 px-1.5 bg-white text-slate-600">
                          {STATUS_TERSIMPAN.map((v) => <option key={v} value={v}>{STATUS_META[v].label}</option>)}
                        </select>
                        <div className="flex items-center gap-0.5">
                          {bolehEdit && (
                            <button onClick={() => bukaEdit(t)} data-testid={`mkt-task-edit-${t.id}`}
                                    className="w-7 h-7 rounded hover:bg-slate-100 text-slate-500 grid place-items-center"><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                          {bolehHapus && (
                            <button onClick={() => hapus(t)} data-testid={`mkt-task-hapus-${t.id}`}
                                    className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {perKolom[s].length === 0 && <p className="text-[11px] text-slate-400 text-center py-5">Kosong</p>}
                </div>
              </div>
            ))}
          </div>
        </LoadingState>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2.5 font-semibold text-left">Nama Task</th>
                  <th className="px-3 py-2.5 font-semibold text-left">Penerima</th>
                  <th className="px-3 py-2.5 font-semibold text-left">Prioritas</th>
                  <th className="px-3 py-2.5 font-semibold text-left">Deadline</th>
                  <th className="px-3 py-2.5 font-semibold text-left">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <LoadingState loading={loading} error={error} onRetry={segarkan} kosong={!loading && !error && halaman.length === 0}
                              varian="tabel" kolom={6} pesanKosong="Belum ada task." data-testid="mkt-task-tabel-state">
                  {halaman.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50/60" data-testid={`mkt-task-row-${t.id}`}>
                      <td className="px-3 py-2.5 cursor-pointer" onClick={() => bukaDetail(t)}
                          data-testid={`mkt-task-t-buka-${t.id}`}>
                        <p className="font-medium text-slate-900 hover:text-emerald-700">{t.nama}</p>
                        {t.deskripsi && <p className="text-[11px] text-slate-500 line-clamp-2">{t.deskripsi}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">
                        <span className="inline-flex items-center gap-1">
                          {t.assignee_type === "role" ? <Users2 className="w-3 h-3" /> : <UserRound className="w-3 h-3" />}
                          {labelPenerima(t)}
                        </span>
                        {/* Kolom "Role" di brief. Untuk task perorangan nilainya
                            TURUNAN dari daftar anggota role saat ini (backend
                            mengirim `roles_pemikul`), jadi ikut berubah begitu
                            orangnya pindah role. */}
                        {t.assignee_type !== "role" && (t.roles_pemikul || []).length > 0 && (
                          <span className="block text-[10px] text-slate-400">{(t.roles_pemikul || []).join(", ")}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5"><Chip meta={PRIORITY_META} value={t.priority} /></td>
                      <td className="px-3 py-2.5 text-xs">
                        {t.deadline
                          ? <span className={t.overdue ? "text-rose-600 font-medium" : "text-slate-600"}>{formatTanggal(t.deadline)}</span>
                          : <span className="text-slate-400">-</span>}
                        {t.reminder_hari_sebelum !== null && t.reminder_hari_sebelum !== undefined && (
                          <span className="block text-[10px] text-slate-400">pengingat H-{t.reminder_hari_sebelum}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5"><Chip meta={STATUS_META} value={t.status_efektif || t.status} /></td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <select value={t.status} onChange={(e) => gantiStatus(t, e.target.value)}
                                  data-testid={`mkt-task-t-status-${t.id}`}
                                  className="h-7 text-[11px] rounded border border-slate-200 px-1.5 bg-white text-slate-600">
                            {STATUS_TERSIMPAN.map((v) => <option key={v} value={v}>{STATUS_META[v].label}</option>)}
                          </select>
                          {bolehEdit && (
                            <button onClick={() => bukaEdit(t)} data-testid={`mkt-task-t-edit-${t.id}`}
                                    className="w-7 h-7 rounded hover:bg-slate-100 text-slate-500 grid place-items-center"><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                          {bolehHapus && (
                            <button onClick={() => hapus(t)} data-testid={`mkt-task-t-hapus-${t.id}`}
                                    className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </LoadingState>
              </tbody>
            </table>
          </div>
          <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-task-paginasi" />
        </div>
      )}

      {/* Form tambah / edit task */}
      <Modal open={!!form} onClose={() => setForm(null)} title={form && form.id ? "Ubah Task" : "Tambah Task"} testid="mkt-task-modal"
             footer={<>
               <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpan} disabled={busy} data-testid="mkt-task-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? "Menyimpan..." : "Simpan"}</button>
             </>}>
        {form && (
          <>
            <Field label="Nama Task" required>
              <input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))}
                     data-testid="mkt-task-f-nama" className={inputCls} />
            </Field>
            <Field label="Deskripsi">
              <textarea value={form.deskripsi} rows={2} onChange={(e) => setForm((f) => ({ ...f, deskripsi: e.target.value }))}
                        data-testid="mkt-task-f-deskripsi" className={textareaCls} />
            </Field>
            <Field label="Ditujukan ke" required
                   hint="Kalau dipilih Role, siapa saja yang memikulnya mengikuti daftar anggota role SAAT INI — anggota baru langsung ikut melihat task lama.">
              <div className="flex gap-2">
                <select value={form.assignee_type} onChange={(e) => setForm((f) => ({ ...f, assignee_type: e.target.value }))}
                        data-testid="mkt-task-f-tipe" className={`${selectCls} w-40`}>
                  <option value="user">Perorangan</option>
                  <option value="role">Role</option>
                </select>
                {form.assignee_type === "user" ? (
                  <select value={form.assignee_user_id} onChange={(e) => setForm((f) => ({ ...f, assignee_user_id: e.target.value }))}
                          data-testid="mkt-task-f-penerima" className={selectCls}>
                    <option value="">— pilih orang —</option>
                    {kandidatItems.map((u) => <option key={u.id} value={u.id}>{u.nama}</option>)}
                  </select>
                ) : (
                  <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                          data-testid="mkt-task-f-role" className={selectCls}>
                    <option value="">— pilih role —</option>
                    {roleItems.map((r) => <option key={r.nama} value={r.nama}>{r.nama}</option>)}
                  </select>
                )}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prioritas">
                <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                        data-testid="mkt-task-f-priority" className={selectCls}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                        data-testid="mkt-task-f-status" className={selectCls}>
                  {STATUS_TERSIMPAN.map((v) => <option key={v} value={v}>{STATUS_META[v].label}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Deadline" hint="Kosongkan kalau tidak ada tenggat.">
                <input type="date" value={form.deadline} onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))}
                       data-testid="mkt-task-f-deadline" className={inputCls} />
              </Field>
              <Field label="Pengingat (H- hari)" hint="Kosongkan kalau tidak perlu diingatkan.">
                <input type="number" min={0} max={60} value={form.reminder_hari_sebelum}
                       onChange={(e) => setForm((f) => ({ ...f, reminder_hari_sebelum: e.target.value }))}
                       data-testid="mkt-task-f-reminder" className={inputCls} placeholder="cth. 1 = sehari sebelum" />
              </Field>
            </div>
            {/* "Terlambat" sengaja TIDAK ada di dropdown Status: ia dihitung dari
                deadline, dan backend menolak menyimpannya (400). */}
            <p className="text-[11px] text-slate-400">
              Status &quot;Terlambat&quot; tidak dipilih manual — sebuah task jadi terlambat karena tenggatnya lewat.
            </p>
          </>
        )}
      </Modal>

      {/* Pengaturan daftar role marketing */}
      {/* M1: detail task — READ-ONLY dan terbuka untuk siapa pun yang berhak
          membacanya menurut backend. Tombol "Ubah" hanya muncul untuk pemegang
          izin `update`, tapi MELIHAT isi tugas tidak pernah butuh izin itu. */}
      <Modal open={!!detailId} onClose={tutupDetail} title="Detail Task" testid="mkt-task-detail-modal"
             footer={
               <div className="flex justify-end gap-2">
                 <button onClick={tutupDetail} data-testid="mkt-task-detail-tutup"
                         className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Tutup</button>
                 {bolehEdit && detail && (
                   <button onClick={() => { const d = detail; tutupDetail(); bukaEdit(d); }}
                           data-testid="mkt-task-detail-ubah"
                           className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">Ubah</button>
                 )}
               </div>
             }>
        {detailGagal && <p className="text-sm text-rose-700" data-testid="mkt-task-detail-gagal">{detailGagal}</p>}
        {!detailGagal && !detail && <p className="text-sm text-slate-500">Memuat...</p>}
        {detail && (
          <div className="space-y-3 text-sm" data-testid="mkt-task-detail-isi">
            <div>
              <p className="font-display font-bold text-base text-slate-900">{detail.nama}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <Chip meta={STATUS_META} value={detail.status_efektif || detail.status} />
                <Chip meta={PRIORITY_META} value={detail.priority} />
              </div>
            </div>
            {detail.deskripsi
              ? <p className="text-slate-700 whitespace-pre-wrap">{detail.deskripsi}</p>
              : <p className="text-slate-400 italic">Tanpa deskripsi.</p>}
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-slate-100">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Ditugaskan ke</p>
                <p className="text-slate-800 inline-flex items-center gap-1">
                  {detail.assignee_type === "role" ? <Users2 className="w-3.5 h-3.5" /> : <UserRound className="w-3.5 h-3.5" />}
                  {labelPenerima(detail)}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Deadline</p>
                <p className={detail.overdue ? "text-rose-600 font-medium" : "text-slate-800"}>
                  {detail.deadline ? formatTanggal(detail.deadline) : "-"}
                  {detail.sisa_hari !== null && detail.sisa_hari !== undefined && !detail.overdue && detail.status !== "completed"
                    ? ` · ${detail.sisa_hari === 0 ? "hari ini" : `H-${detail.sisa_hari}`}` : ""}
                </p>
              </div>
              {detail.reminder_hari_sebelum !== null && detail.reminder_hari_sebelum !== undefined && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">Pengingat</p>
                  <p className="text-slate-800">H-{detail.reminder_hari_sebelum}</p>
                </div>
              )}
              {detail.created_by_name && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">Dibuat oleh</p>
                  <p className="text-slate-800">{detail.created_by_name}</p>
                </div>
              )}
            </div>
            {/* Pemikul task BOLEH menggeser status walau tidak punya izin
                `update` — itu sudah ditegakkan backend (`update_task`). */}
            <div className="pt-2 border-t border-slate-100">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Ubah status</p>
              <select value={detail.status}
                      onChange={async (e) => { await gantiStatus(detail, e.target.value); setDetail({ ...detail, status: e.target.value }); }}
                      data-testid="mkt-task-detail-status"
                      className={`${selectCls} h-9 w-auto`}>
                {STATUS_TERSIMPAN.map((v) => <option key={v} value={v}>{STATUS_META[v].label}</option>)}
              </select>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!roleForm} onClose={() => setRoleForm(null)} title="Atur Role Marketing" maxWidth="max-w-2xl" testid="mkt-task-role-modal"
             footer={<>
               <button onClick={() => setRoleForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={() => simpanRole(false)} disabled={busy} data-testid="mkt-task-role-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? "Menyimpan..." : "Simpan"}</button>
             </>}>
        {roleForm && (
          <>
            <p className="text-xs text-slate-500">
              Daftar ini yang muncul di dropdown &quot;Role&quot;. Anggotanya menentukan siapa yang melihat task
              ber-role — dihitung setiap kali daftar dibuka, jadi anggota baru langsung ikut melihat task lama.
            </p>
            <div className="space-y-2">
              {roleForm.items.map((r, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-2.5 space-y-2" data-testid={`mkt-task-role-baris-${i}`}>
                  <div className="flex items-center gap-2">
                    <input value={r.nama} onChange={(e) => setRoleForm((f) => ({
                      ...f, items: f.items.map((x, j) => (j === i ? { ...x, nama: e.target.value } : x)),
                    }))} data-testid={`mkt-task-role-nama-${i}`} className={inputCls} placeholder="Nama role" />
                    <button onClick={() => setRoleForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}
                            data-testid={`mkt-task-role-hapus-${i}`}
                            className="w-9 h-9 shrink-0 rounded-lg hover:bg-rose-50 text-rose-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {kandidatItems.map((u) => {
                      const aktif = (r.user_ids || []).includes(u.id);
                      return (
                        <button key={u.id} data-testid={`mkt-task-role-${i}-anggota-${u.id}`}
                                onClick={() => setRoleForm((f) => ({
                                  ...f,
                                  items: f.items.map((x, j) => (j === i ? {
                                    ...x,
                                    user_ids: aktif ? (x.user_ids || []).filter((v) => v !== u.id) : [...(x.user_ids || []), u.id],
                                  } : x)),
                                }))}
                                className={`text-[11px] px-2 py-1 rounded-full border ${aktif ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                          {u.nama}
                        </button>
                      );
                    })}
                    {kandidatItems.length === 0 && <p className="text-[11px] text-slate-400">Daftar pengguna belum termuat.</p>}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setRoleForm((f) => ({ ...f, items: [...f.items, { nama: "", user_ids: [] }] }))}
                    data-testid="mkt-task-role-tambah"
                    className="h-9 px-3 rounded-lg border border-dashed border-slate-300 text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Tambah Role
            </button>
          </>
        )}
      </Modal>

      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(segarkan)} />
    </div>
  );
}
